import {
	getMergeTagLabel,
	normalizeDynamicTextMaxLength,
	parseDynamicTextHelperExpression,
	pickBadgeDisplayText,
	serializeDynamicTextHelperExpression,
} from '@bay-email/core';

function formatMergeTagLabel(key: string): string {
	// Take last segment of dot-notation: "contact.firstName" → "firstName"
	const parts = key.split('.');
	const fieldPart = parts[parts.length - 1] || key;
	return fieldPart
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim()
		.replace(/\s+/g, ' ')
		.replace(/\b\w/g, c => c.toUpperCase());
}

const LINK_MERGE_TAG_KEYS = new Set([
	'listing.url',
	'user.websiteUrl',
	'user.website',
	'user.companyWebsite',
	'user.teamWebsiteAddress',
	'user.officeDomain',
	'unsubscribeLink',
	'legal_compliance.unsubscribeLink',
]);

function isLinkMergeTagKey(tagKey: string): boolean {
	return LINK_MERGE_TAG_KEYS.has(String(tagKey || '').trim());
}

// The fallback is captured out of an HTML string: `revert()` writes the
// `{{else}}…{{/if}}` template through a text node, so a fallback the user typed
// as "Smith & Sons" is stored — and re-captured by `transform` — as
// "Smith &amp; Sons". That was harmless while the fallback only ever travelled
// back out as HTML; now that the chip DISPLAYS it as text, it has to be decoded
// first or the canvas prints entity noise the delivered email does not.
//
// A textarea's innerHTML->value round trip decodes entities without executing
// anything, which is why it is the one element used here.
function decodeHtmlText(value: string): string {
	const holder = document.createElement('textarea');
	holder.innerHTML = String(value || '');
	return holder.value;
}

function escapeHtmlText(value: string): string {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function createBadgeInput(
	tagKey: string,
	tagFallback: string,
	id?: string,
	tagFormat?: string,
	tagMaxLength?: number | null,
	resolvedDisplayValue?: string,
): string {
	// A span, not an <input type="button">. An input's value is a single line by
	// definition — a long resolved value (a certifications list, a URL) ran off
	// the edge of its column on the canvas and no CSS could wrap it. A span holds
	// real text, so it wraps; contenteditable="false" keeps it an atomic,
	// un-typeable chip inside the editable region, which is what the input gave
	// us for free.
	const input = document.createElement('span');
	input.className = 'bay-email-merge-tag';
	input.setAttribute('contenteditable', 'false');
	if (id) input.id = id;
	// A resolved real value takes the badge's displayed text — formatted the same
	// way the tag's own format/max-length config would render it elsewhere — and
	// the chip stays locked/underlined/clickable-to-configure, it just shows real
	// data instead of the field label once that data is available (e.g. the
	// current viewer's real license number instead of "License Number").
	// Otherwise prefer the curated dictionary label ("Listing URL") over
	// last-segment humanization, which turns generic leaf keys like "listing.url"
	// into a context-less "Url". Falls back to the humanized last segment for
	// keys the dictionary doesn't cover.
	// Decoded for DISPLAY only — the attribute below keeps the captured form, so
	// the popover and revert() round-trip is unchanged.
	const displayText = pickBadgeDisplayText(
		resolvedDisplayValue,
		decodeHtmlText(tagFallback),
		getMergeTagLabel(tagKey) ?? formatMergeTagLabel(tagKey),
		tagFormat,
		tagMaxLength,
	);
	input.textContent = displayText;
	// Mirrored onto `value` so the raster builder, which reads it off the chip,
	// keeps working now that this is no longer a form control.
	input.setAttribute('value', displayText);
	input.setAttribute('data-tag-key', tagKey);
	if (tagFallback) input.setAttribute('data-tag-fallback', tagFallback);
	if (tagFormat && tagFormat !== 'string') {
		input.setAttribute('data-tag-format', tagFormat);
	}
	const normalizedMaxLength = normalizeDynamicTextMaxLength(tagMaxLength);
	if (normalizedMaxLength !== null) {
		input.setAttribute('data-tag-max-length', String(normalizedMaxLength));
	}
	return input.outerHTML;
}

// A captured anchor "link text" that is just a bare {{token}} is the full-value
// default (e.g. a URL link showing its own URL), not a custom label. Storing it
// as data-tag-fallback re-embeds a raw {{token}} in the badge attribute, which
// the catch-all {{...}} rule below then turns into a nested <input> — corrupting
// the tag and leaking a stray `">` into the canvas. Drop it in that case.
const sanitizeCapturedFallback = (text: string) => {
	const trimmed = String(text || '').trim();
	return /^\{\{[^{}]+\}\}$/.test(trimmed) ? '' : trimmed;
};

// Runs `replacer` over the text between tags and hands every `<...>` back
// untouched, so a substitution can never land inside an attribute value.
const replaceOutsideTags = (html: string, replacer: (chunk: string) => string) =>
	html.replace(/<[^>]*>|[^<]+/g, segment =>
		segment.startsWith('<') ? segment : replacer(segment),
	);

const transform = (
	text: string,
	id?: string,
	resolveDisplayValue?: (tagKey: string) => string | undefined,
) => {
	// Handle link-style conditional tags produced by link merge-tag serialization.
	let result = text.replace(
		/\{\{#if\s+([\w.-]+)\}\}\s*<a[^>]*href=(?:"|')\s*\{\{([^{}]+?)\}\}\s*(?:"|')[^>]*>([\s\S]*?)<\/a>\s*(?:\{\{else\}\}([\s\S]*?))?\s*\{\{\/if\}\}/gi,
		(_, conditionKey, hrefExpression, linkText, elseText = '') => {
			const parsedHelper = parseDynamicTextHelperExpression(
				String(hrefExpression || '').trim(),
			);
			const tagKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!tagKey) return '';

			const fallbackText = sanitizeCapturedFallback(linkText || elseText);
			return createBadgeInput(
				tagKey,
				fallbackText,
				id,
				parsedHelper?.format,
				parsedHelper?.maxLength,
				resolveDisplayValue?.(tagKey),
			);
		},
	);

	// Backward compatibility: handle escaped literal anchor tags from older serialization.
	result = result.replace(
		/\{\{#if\s+([\w.-]+)\}\}\s*&lt;a[\s\S]*?href=(?:"|')\s*\{\{([^{}]+?)\}\}\s*(?:"|')[\s\S]*?&gt;([\s\S]*?)&lt;\/a&gt;\s*(?:\{\{else\}\}([\s\S]*?))?\s*\{\{\/if\}\}/gi,
		(_, conditionKey, hrefExpression, linkText, elseText = '') => {
			const parsedHelper = parseDynamicTextHelperExpression(
				String(hrefExpression || '').trim(),
			);
			const tagKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!tagKey) return '';

			const fallbackText = sanitizeCapturedFallback(linkText || elseText);
			return createBadgeInput(
				tagKey,
				fallbackText,
				id,
				parsedHelper?.format,
				parsedHelper?.maxLength,
				resolveDisplayValue?.(tagKey),
			);
		},
	);

	// Handle link merge tags that already exist as anchors in the editor DOM.
	result = result.replace(
		/<a\b[^>]*href=(?:"|')\s*\{\{([^{}]+?)\}\}\s*(?:"|')[^>]*>([\s\S]*?)<\/a>/gi,
		(match, hrefExpression, linkText) => {
			const parsedHelper = parseDynamicTextHelperExpression(
				String(hrefExpression || '').trim(),
			);
			const tagKey = String(parsedHelper?.key || hrefExpression || '').trim();
			if (!tagKey || !isLinkMergeTagKey(tagKey)) return match;

			return createBadgeInput(
				tagKey,
				sanitizeCapturedFallback(linkText),
				id,
				parsedHelper?.format,
				parsedHelper?.maxLength,
				resolveDisplayValue?.(tagKey),
			);
		},
	);

	// Handle {{#if KEY}}{{KEY}}{{else}}FALLBACK{{/if}} and helper variants.
	result = result.replace(
		/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g,
		(_, conditionKey, trueExpression, fallbackText) => {
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const tagKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!tagKey) return '';
			return createBadgeInput(
				tagKey,
				String(fallbackText || '').trim(),
				id,
				parsedHelper?.format,
				parsedHelper?.maxLength,
				resolveDisplayValue?.(tagKey),
			);
		},
	);

	// Handle plain {{KEY}}, helper tags, plus legacy {{KEY|FALLBACK}} for backwards compat.
	//
	// Text nodes only. A token sitting in an ATTRIBUTE (<img src="{{user.logo}}">,
	// <td background="{{user.brandPrimaryColor}}">) renders nothing on its own, but
	// swapping it for a badge injects `<` and `"` mid-attribute — which closes the
	// element early and takes it, plus everything after it, out of the document.
	// The three rules above deliberately span tags and still run on the whole
	// string; only this catch-all is restricted.
	result = replaceOutsideTags(result, chunk =>
		chunk.replace(/\{\{([\s\S]+?)\}\}/g, (_, $1) => {
		const raw = String($1).trim();
		// Skip any orphaned Handlebars block tokens
		if (raw.startsWith('#') || raw.startsWith('/') || raw === 'else') return '';

		const parsedHelper = parseDynamicTextHelperExpression(raw);
		if (parsedHelper) {
			return createBadgeInput(
				parsedHelper.key,
				'',
				id,
				parsedHelper.format,
				parsedHelper.maxLength,
				resolveDisplayValue?.(parsedHelper.key),
			);
		}

		if (/\s/.test(raw)) {
			return `{{${raw}}}`;
		}

		const pipeIdx = raw.indexOf('|');
		const tagKey = pipeIdx >= 0 ? raw.slice(0, pipeIdx).trim() : raw;
		const tagFallback = pipeIdx >= 0 ? raw.slice(pipeIdx + 1).trim() : '';

		return createBadgeInput(
			tagKey,
			tagFallback,
			id,
			undefined,
			undefined,
			resolveDisplayValue?.(tagKey),
		);
		}),
	);

	return result;
};
export class MergeTagBadge {
	static transform(
		content: string,
		id?: string,
		resolveDisplayValue?: (tagKey: string) => string | undefined,
	) {
		const loop = (node: ChildNode) => {
			if (node instanceof HTMLElement) {
				if (node.textContent === node.innerHTML) {
					node.innerHTML = transform(node.innerHTML, id, resolveDisplayValue);
				} else {
					[...node.childNodes].forEach(loop);
				}
			} else {
				if (node.nodeType === 3 && node.textContent) {
					const div = document.createElement('div');
					div.innerHTML = transform(node.textContent, id, resolveDisplayValue);
					node.replaceWith(...div.childNodes);
				}
			}
		};
		const container = document.createElement('div');
		container.innerHTML = content;
		container.innerHTML = transform(container.innerHTML, id, resolveDisplayValue);

		[...container.childNodes].forEach(loop);
		return container.innerHTML;
	}

	static revert(content: string, generateMergeTag: (s: string) => string) {
		const container = document.createElement('div');
		container.innerHTML = content;
		container.querySelectorAll('.bay-email-merge-tag').forEach((item: any) => {
			const tagKey = item.getAttribute('data-tag-key') || item.value;
			const tagFallback = item.getAttribute('data-tag-fallback') || '';
			const tagFormat = item.getAttribute('data-tag-format') || undefined;
			const tagMaxLength = normalizeDynamicTextMaxLength(
				item.getAttribute('data-tag-max-length'),
			);

			const helperExpression = serializeDynamicTextHelperExpression(
				tagKey,
				tagFormat,
				tagMaxLength,
			);
			const plainExpression = generateMergeTag(tagKey);
			const isFormattedHelperExpression = helperExpression !== `{{${tagKey}}}`;
			const trueBranchExpression = isFormattedHelperExpression
				? helperExpression
				: plainExpression;

			let templateString = tagFallback
				? `{{#if ${tagKey}}}${trueBranchExpression}{{else}}${tagFallback}{{/if}}`
				: isFormattedHelperExpression
					? `{{#if ${tagKey}}}${trueBranchExpression}{{/if}}`
					: trueBranchExpression;

			if (isLinkMergeTagKey(tagKey)) {
				// URL-type link fields (Listing URL, Website, Domain) show the FULL URL as
				// the clickable text — mirroring the signature's full-email link — instead
				// of a friendly label ("URL dynamic fields unnecessarily formatted in text
				// blocks" bug). An explicit user-entered link text still wins. Unsubscribe
				// keeps a label: never print the raw unsubscribe URL.
				const isUnsubscribeLink = tagKey.toLowerCase().includes('unsubscribe');
				const linkText = tagFallback
					? escapeHtmlText(tagFallback)
					: isUnsubscribeLink
						? escapeHtmlText(formatMergeTagLabel(tagKey))
						: trueBranchExpression;
				const emptyBranch = tagFallback
					? escapeHtmlText(tagFallback)
					: isUnsubscribeLink
						? escapeHtmlText(formatMergeTagLabel(tagKey))
						: '';
				templateString = `{{#if ${tagKey}}}<a href="${trueBranchExpression}" target="_blank" rel="noopener noreferrer" style="color: inherit;">${linkText}</a>{{else}}${emptyBranch}{{/if}}`;

				const parentNode = item.parentNode;
				if (parentNode) {
					const wrapper = document.createElement('span');
					wrapper.innerHTML = templateString;
					while (wrapper.firstChild) {
						parentNode.insertBefore(wrapper.firstChild, item);
					}
					parentNode.removeChild(item);
				}
				return;
			}

			item.parentNode?.replaceChild(document.createTextNode(templateString), item);
		});

		return container.innerHTML;
	}
}
